/**
 * 分拣看板：仓库类型切换（与 GraphQL business_type 一致，1=分拣仓，2=尾程仓）
 */
export type SortingDashboardBusinessTypeTab = 'lastMileHub' | 'sortingHub';

/** 「次日交接率」= next_day_handover_count / inbound_count，由后端计算返回 */
export const NEXT_DAY_HANDOVER_RATE_TOOLTIP = 'T日入库包裹中，在T+1 23:59前交接的比例';

/**
 * 分拣仓 tab 下按业务类型拆分交接率的 4 个尾程分拣仓；LAXSTEMU 不拆。
 * 4 仓的「次日交接率」一行拆为「FBT 当日交接率」+「CBT 次日交接率」两行，
 * 数据取自 customerSlices 中对应 customerName 的切片。
 */
export const SORTING_HUB_HANDOVER_SPLIT_CODES: ReadonlySet<string> = new Set(['LAXSCBT', 'LAXS', 'EWRS', 'HOUS']);

/**
 * 分拣仓 tab 下业务类型下拉选项（替代原客户名下拉）；
 * value 是 Holo `sorting_dashboard_hourly_summary.customer_name` 字面值（与 channel.channel_name 一致），
 * label 是 UI 展示的业务类型简称。
 */
export const SORTING_HUB_BIZ_TYPE_OPTIONS: ReadonlyArray<{ value: string | null; label: string }> = [
    { value: null, label: '全部' },
    { value: 'TikTok CBT', label: 'CBT' },
    { value: 'TikTok FBT', label: 'FBT' },
    { value: 'TEMU-USPS', label: 'TEMU-USPS' },
];

/** 拆行时用于查 customerSlices 的 customer_name key（TikTok FBT / TikTok CBT，与 channel_name 一致） */
export const SORTING_HUB_SLICE_CUSTOMER_FBT = 'TikTok FBT';
export const SORTING_HUB_SLICE_CUSTOMER_CBT = 'TikTok CBT';
export const SORTING_HUB_SLICE_CUSTOMER_TEMU = 'TEMU-USPS';

/**
 * 分拣仓 tab 主查询附带的 customerNames，触发后端按 customer_name 切片返回；
 * 与 Holo `sorting_dashboard_hourly_summary.customer_name` 字面值一致（channel_name 形式）。
 * 首项空串会被后端翻成 null（不按 customer_name 过滤），对应"全客户合计"切片；
 * assembleSlices 用第一个切片填充 items/aggregate，所以必须放首位，保证主表 hub 行
 * 的入库包裹量等字段仍是真实全仓合计而不是单一 customer 的子切片。
 */
export const SORTING_HUB_SLICE_CUSTOMER_NAMES: ReadonlyArray<string> = [
    '',
    SORTING_HUB_SLICE_CUSTOMER_FBT,
    SORTING_HUB_SLICE_CUSTOMER_CBT,
    SORTING_HUB_SLICE_CUSTOMER_TEMU,
];

/** 分拣仓 4 仓 FBT 当日交接率 tooltip：与 PRD 一致 */
export const FBT_SAME_DAY_HANDOVER_TOOLTIP = '当日入库包裹中，当日晚上23:59前交接的包裹比例';

/** 分拣仓 4 仓 CBT 次日交接率 tooltip：与 PRD 一致 */
export const CBT_NEXT_DAY_HANDOVER_TOOLTIP = '当日入库包裹中，次日中午11:59前交接的包裹比例';

/** 「最终未交接率」复用后端 shouldArriveNotArriveRate 字段，分拣仓 tab 重命名展示 */
export const FINAL_NOT_HANDOVER_RATE_TOOLTIP = 'T日入库包裹中，截止当前仍未交接完成的包裹比例';

/**
 * 尾程仓 tab 新增「整体漏扫率」父指标，由后端算：
 * 分子 = ascan_miss_count + transfer_arrival_miss_count；
 * 分母 = dscan_count + transfer_arrival_dscan_count。
 */
export const OVERALL_MISS_SCAN_RATE_TOOLTIP =
    '司机扫描或手动领取的包裹中，ASCAN 漏扫 + 转运到仓漏扫合并统计的漏扫率';

/** LAXS/LAXSCBT/EWRS 三个 Tiktok 分拣仓共用的「完成交接」节点定义 */
const TIKTOK_HANDOVER_DEFINITION =
    '完成交接的定义：\n' +
    '  尾程是我们：有该仓装包扫描，及该仓的装车扫描\n' +
    '    LAXSCBT -> LAX 或 EWRS -> EWR：只需要上述节点；其他场景：还需要hub departed\n' +
    '  尾程非我们：有该仓入库扫描，该仓的装车扫描，及该仓的发车扫描';

const TEMU_HANDOVER_DEFINITION = '完成交接：有handover节点';

interface SortingHubTooltipSpec {
    /** 订单范围文案 */
    orderScope: string;
    /** 完成交接节点定义（多行，已含「完成交接：」前缀） */
    handoverDefinition: string;
}

/** 分拣仓 tab 下四个 hub 的 tooltip 分段规格，键与 sortingCode 一致 */
const SORTING_HUB_TOOLTIP_SPECS: Record<string, SortingHubTooltipSpec> = {
    LAXS: {
        orderScope: '客户为Tiktok CBT、Tiktok FBT',
        handoverDefinition: TIKTOK_HANDOVER_DEFINITION,
    },
    LAXSCBT: {
        orderScope: '客户为Tiktok CBT、Tiktok FBT',
        handoverDefinition: TIKTOK_HANDOVER_DEFINITION,
    },
    EWRS: {
        orderScope: '客户为Tiktok CBT、Tiktok FBT',
        handoverDefinition: TIKTOK_HANDOVER_DEFINITION,
    },
    LAXSTEMU: {
        orderScope: '客户为TEMU USPS',
        handoverDefinition: TEMU_HANDOVER_DEFINITION,
    },
};

/** 分拣仓 tab 下按 hub 组合次日交接率 tooltip；未匹配到 hub 时回退到通用文案 */
export function getSortingHubNextDayHandoverTooltip(sortingCode: string | undefined): string {
    const spec = sortingCode ? SORTING_HUB_TOOLTIP_SPECS[sortingCode] : undefined;
    if (!spec) return NEXT_DAY_HANDOVER_RATE_TOOLTIP;
    return `T日入库包裹中，在T+1 23:59前交接的比例\n订单范围：${spec.orderScope}\n${spec.handoverDefinition}`;
}

/** 分拣仓 tab 下按 hub 组合最终未交接率 tooltip；未匹配到 hub 时回退到通用文案 */
export function getSortingHubFinalNotHandoverTooltip(sortingCode: string | undefined): string {
    const spec = sortingCode ? SORTING_HUB_TOOLTIP_SPECS[sortingCode] : undefined;
    if (!spec) return FINAL_NOT_HANDOVER_RATE_TOOLTIP;
    return `T日入库包裹中，截止当前仍未交接完成的包裹比例\n订单范围：${spec.orderScope}\n${spec.handoverDefinition}`;
}
